import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { RangeSnapshot, RangeUser } from '~/lib/api/types'

export const ENGINEERS_PARAM = 'engineers'
export const OOO_PARAM = 'ooo'
export const PARSE_FAILED_PARAM = 'parse_failed'

export type CalendarFilters = {
  engineers: ReadonlySet<string>
  oooOnly: boolean
  parseFailedOnly: boolean
}

export const EMPTY_FILTERS: CalendarFilters = {
  engineers: new Set<string>(),
  oooOnly: false,
  parseFailedOnly: false,
}

export function useCalendarFilters(): {
  filters: CalendarFilters
  setEngineers: (slackIds: ReadonlyArray<string>) => void
  toggleEngineer: (slackId: string) => void
  setOooOnly: (on: boolean) => void
  setParseFailedOnly: (on: boolean) => void
  clearAll: () => void
  activeCount: number
} {
  const [params, setParams] = useSearchParams()

  const filters = useMemo<CalendarFilters>(() => readFilters(params), [params])

  const writeParams = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(params)
      mutate(next)
      setParams(next, { replace: true })
    },
    [params, setParams]
  )

  const setEngineers = useCallback(
    (slackIds: ReadonlyArray<string>) => {
      writeParams(next => {
        if (slackIds.length === 0) {
          next.delete(ENGINEERS_PARAM)
        } else {
          next.set(ENGINEERS_PARAM, slackIds.join(','))
        }
      })
    },
    [writeParams]
  )

  const toggleEngineer = useCallback(
    (slackId: string) => {
      const current = new Set(filters.engineers)
      if (current.has(slackId)) {
        current.delete(slackId)
      } else {
        current.add(slackId)
      }
      setEngineers(Array.from(current))
    },
    [filters.engineers, setEngineers]
  )

  const setOooOnly = useCallback(
    (on: boolean) => {
      writeParams(next => {
        if (on) next.set(OOO_PARAM, '1')
        else next.delete(OOO_PARAM)
      })
    },
    [writeParams]
  )

  const setParseFailedOnly = useCallback(
    (on: boolean) => {
      writeParams(next => {
        if (on) next.set(PARSE_FAILED_PARAM, '1')
        else next.delete(PARSE_FAILED_PARAM)
      })
    },
    [writeParams]
  )

  const clearAll = useCallback(() => {
    writeParams(next => {
      next.delete(ENGINEERS_PARAM)
      next.delete(OOO_PARAM)
      next.delete(PARSE_FAILED_PARAM)
    })
  }, [writeParams])

  const activeCount = countActive(filters)

  return {
    filters,
    setEngineers,
    toggleEngineer,
    setOooOnly,
    setParseFailedOnly,
    clearAll,
    activeCount,
  }
}

export function readFilters(params: URLSearchParams): CalendarFilters {
  const raw = params.get(ENGINEERS_PARAM)
  const engineers = raw
    ? new Set(
        raw
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      )
    : new Set<string>()
  return {
    engineers,
    oooOnly: params.get(OOO_PARAM) === '1',
    parseFailedOnly: params.get(PARSE_FAILED_PARAM) === '1',
  }
}

export function countActive(filters: CalendarFilters): number {
  let n = 0
  if (filters.engineers.size > 0) n += 1
  if (filters.oooOnly) n += 1
  if (filters.parseFailedOnly) n += 1
  return n
}

export function applyFilters(
  snapshot: RangeSnapshot,
  filters: CalendarFilters
): RangeSnapshot {
  if (countActive(filters) === 0) return snapshot

  const users = snapshot.users.filter(user =>
    userMatches(user, snapshot, filters)
  )
  return { ...snapshot, users }
}

function userMatches(
  user: RangeUser,
  snapshot: RangeSnapshot,
  filters: CalendarFilters
): boolean {
  if (
    filters.engineers.size > 0 &&
    !filters.engineers.has(user.slack_user_id)
  ) {
    return false
  }
  if (filters.oooOnly && !userHasCellKind(user, snapshot, 'ooo')) {
    return false
  }
  if (
    filters.parseFailedOnly &&
    !userHasCellKind(user, snapshot, 'parse_failed')
  ) {
    return false
  }
  return true
}

function userHasCellKind(
  user: RangeUser,
  snapshot: RangeSnapshot,
  kind: 'ooo' | 'parse_failed'
): boolean {
  return snapshot.days.some(day =>
    day.cells.some(cell => cell.user_id === user.id && cell.kind === kind)
  )
}
